<template>
  <div class="viq-compare">
    <div class="viq-compare__header">
      <h3 class="viq-compare__title">Compare Markets</h3>
      <el-button v-if="step === 'form'" size="small" @click="goBack">Back</el-button>
    </div>

    <!-- Form -->
    <div v-if="step === 'form'" class="viq-compare__form">
      <p class="viq-compare__desc">
        Compare regulatory frameworks across multiple jurisdictions for strategic planning.
      </p>

      <div class="viq-compare__field">
        <label>Jurisdictions to Compare</label>
        <div class="viq-compare__jurisdictions">
          <div
            v-for="(j, i) in jurisdictions"
            :key="i"
            class="viq-compare__jurisdiction"
          >
            <el-input
              v-model="jurisdictions[i]"
              size="small"
              placeholder="e.g., United Kingdom"
            />
            <button
              v-if="jurisdictions.length > 2"
              class="viq-compare__remove"
              @click="removeJurisdiction(i)"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <button class="viq-compare__add" @click="addJurisdiction">
            + Add jurisdiction
          </button>
        </div>
      </div>

      <div class="viq-compare__field">
        <label>Focus Area</label>
        <el-input
          v-model="focusArea"
          placeholder="e.g., Licensing requirements for payment institutions"
        />
      </div>

      <div class="viq-compare__form-actions">
        <el-button
          type="primary"
          :disabled="!canSubmit"
          @click="generate"
        >
          Compare Markets
        </el-button>
      </div>
    </div>

    <!-- Results -->
    <div v-else class="viq-compare__results">
      <div v-if="isStreaming" class="viq-compare__streaming">
        <span class="viq-compare__spinner" />
        Comparing markets...
      </div>
      <div
        v-if="streamingText || reportContent"
        class="viq-compare__content"
        v-html="renderedContent"
      />
      <div v-if="!isStreaming" class="viq-compare__actions">
        <el-button type="primary" @click="$emit('export-pdf')">Export as PDF</el-button>
        <el-button v-if="$config.features && $config.features.viqShare" @click="share">Save &amp; Share</el-button>
        <el-button @click="reset">New Comparison</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import DOMPurify from "dompurify";

export default {
  name: "ViqCompareMarkets",
  data() {
    return {
      step: "form",
      jurisdictions: ["", ""],
      focusArea: "",
      reportContent: "",
    };
  },
  computed: {
    ...mapState("Viq", ["isStreaming", "streamingText"]),
    canSubmit() {
      return this.jurisdictions.filter((j) => j.trim()).length >= 2;
    },
    renderedContent() {
      const text = this.reportContent || this.streamingText || "";
      if (!text) return "";
      let html = text;
      html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html = html.replace(/^### (.+)$/gm, "<h3>$1</h3>");
      html = html.replace(/^## (.+)$/gm, "<h2>$1</h2>");
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
      html = html.replace(/^[*-] (.+)$/gm, "<li>$1</li>");
      html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>");
      html = html.replace(/\n\n/g, "</p><p>");
      html = "<p>" + html + "</p>";
      html = html.replace(/<p><\/p>/g, "");
      html = html.replace(/\n/g, "<br>");
      return DOMPurify.sanitize(html);
    },
  },
  watch: {
    isStreaming(val, old) {
      if (old && !val && this.step === "results") {
        this.reportContent = this.streamingText;
      }
    },
  },
  methods: {
    goBack() {
      this.$store.commit("Viq/SET_REPORT_STEP", 0);
      this.$store.commit("Viq/SET_REPORT_TYPE", null);
    },
    addJurisdiction() {
      this.jurisdictions.push("");
    },
    removeJurisdiction(i) {
      this.jurisdictions.splice(i, 1);
    },
    generate() {
      this.step = "results";
      const validJurisdictions = this.jurisdictions.filter((j) => j.trim());
      this.$store.dispatch("Viq/streamReport", {
        conversationId: null,
        sections: [
          {
            title: "Market Comparison",
            jurisdictions: validJurisdictions,
            focus_area: this.focusArea.trim() || undefined,
            type: "compare-markets",
          },
        ],
      });
    },
    share() {
      this.$store.dispatch("Viq/openShareDialog", {
        type: "compare-markets",
        content: this.reportContent,
      });
    },
    reset() {
      this.step = "form";
      this.jurisdictions = ["", ""];
      this.focusArea = "";
      this.reportContent = "";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-compare {
  max-width: 720px;
  margin: 0 auto;
  width: 100%;
  padding: $viq-space-lg 0;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $viq-space-lg;
  }

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0;
  }

  &__desc {
    font-size: $viq-font-base;
    color: $viq-text-secondary;
    margin: 0 0 $viq-space-lg;
    line-height: 1.5;
  }

  &__form {
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-lg;
  }

  &__field {
    margin-bottom: $viq-space-md;

    label {
      display: block;
      font-size: $viq-font-sm;
      font-weight: 600;
      color: $viq-text-primary;
      margin-bottom: $viq-space-sm;
    }
  }

  &__jurisdictions {
    display: flex;
    flex-direction: column;
    gap: $viq-space-sm;
  }

  &__jurisdiction {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
  }

  &__remove {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    color: $viq-text-secondary;
    cursor: pointer;
    border-radius: $viq-radius-sm;

    &:hover {
      background: rgba(239, 68, 68, 0.1);
      color: $viq-error;
    }
  }

  &__add {
    align-self: flex-start;
    padding: $viq-space-sm $viq-space-md;
    border: 1px dashed $viq-border;
    border-radius: $viq-radius-sm;
    background: none;
    color: $viq-text-secondary;
    font-size: $viq-font-sm;
    cursor: pointer;

    &:hover {
      border-color: $viq-accent;
      color: $viq-accent;
    }
  }

  &__form-actions {
    margin-top: $viq-space-lg;
    display: flex;
    justify-content: flex-end;
  }

  &__streaming {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-md;
    color: $viq-text-secondary;
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__content {
    font-size: $viq-font-base;
    line-height: 1.7;
    color: $viq-text-primary;
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;

    ::v-deep {
      h2, h3 { margin: $viq-space-lg 0 $viq-space-sm; font-weight: 700; }
      p { margin: 0 0 $viq-space-sm; }
      ul { padding-left: $viq-space-lg; margin: $viq-space-sm 0; }
      li { margin-bottom: $viq-space-xs; }
      strong { font-weight: 700; }
    }
  }

  &__actions {
    display: flex;
    gap: $viq-space-sm;
    margin-top: $viq-space-lg;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
